import {
  Button,
  Card,
  Checkbox,
  Label,
  Page,
  Stack,
  TextField,
  Text,
  Divider,
  FlatActionList,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { GenerateProductDescriptionFormValues } from "../../components/GenerateProductDescriptionForm";
import { useTranslation } from "../../../../utilities/i18n";
import { Colors, Spacings, View } from "react-native-ui-lib";
import {
  ErrorCode,
  ExecuteLlmQuickWinResult,
  GenerateOn,
} from "@towersystems/roam-common/lib/generated-types";
import { StackScreenProps } from "@react-navigation/stack";
import {
  GenerateProductDescriptionRootParamList,
  ProductDescriptionLlmQuickWinRootParamList,
} from "../../../../types";
import { useMutation } from "@apollo/client";
import { MUTATION_EXECUTE_QUICK_WIN } from "../../../../utilities/llm-quick-wins";
import { Ionicons } from "@expo/vector-icons";
import Editor from "react-simple-wysiwyg";
import { ScreenContext } from "./common";

const GenerateFromOptions = [
  {
    label: "Product Name",
    value: GenerateOn.PRODUCT_NAME,
    disabled: true,
  },
  {
    label: "Features & Tags",
    value: GenerateOn.PRODUCT_FEATURES_AND_TAGS,
  },
  {
    label: "Category",
    value: GenerateOn.PRODUCT_CATEGORY,
  },
  {
    label: "Department",
    value: GenerateOn.PRODUCT_DEPARTMENT,
  },
  {
    label: "Current Description",
    value: GenerateOn.PRODUCT_CURRENT_DESCRIPTION,
  },
];

export interface ProductDescriptionLlmQuickWinScreenProps
  extends StackScreenProps<
    GenerateProductDescriptionRootParamList &
      ProductDescriptionLlmQuickWinRootParamList,
    "ProductDescriptionLlmQuickWinScreen"
  > {}

export const ProductDescriptionLlmQuickWinScreen = ({
  navigation,
}: ProductDescriptionLlmQuickWinScreenProps) => {
  const [generatedDescription, setGeneratedDescription] = React.useState<
    string | undefined
  >(undefined);

  const { setProductDescription } = React.useContext(ScreenContext);

  const { control, watch, reset, formState, handleSubmit } =
    useFormContext<GenerateProductDescriptionFormValues>();

  const { t } = useTranslation("product");

  const [m] = useMutation<{
    executeLlmQuickWin: ExecuteLlmQuickWinResult;
  }>(MUTATION_EXECUTE_QUICK_WIN);
  const flashMessagesContext = useFlashMessages();

  const { isLoading, isSubmitting, isValid, isSubmitSuccessful } = formState;

  const watchedToneOfVoice = watch("toneOfVoice");

  const disabled = isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      reset(undefined, {
        keepValues: true,
      });
    }
  }, [isSubmitSuccessful, reset]);

  const handleOnSubmit = async (
    input: GenerateProductDescriptionFormValues
  ) => {
    return m({
      variables: {
        input: {
          product_description_quick_win: {
            productName: input.productName,
            productFeaturesAndTags: input.productFeaturesAndTags,
            numberOfSentences: input.numberOfSentences,
            toneOfVoice: input.toneOfVoice,
            generateOn: input.generateOn,
            currentDescription: input.existingDescription,
            categoryId: input.categoryId,
            departmentId: input.departmentId,
          },
        },
      },
    })
      .then((response) => {
        const result = response.data?.executeLlmQuickWin;
        if (result && "result" in result) {
          setGeneratedDescription(result.result);
        } else {
          if (result && "message" in result) {
            throw new Error(result.message);
          }
          flashMessagesContext.showMessage({
            message: "Failed to generate description. Check your credentials.",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        const msg = error?.message ?? "Failed to generate description";

        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });

        if (
          [
            ErrorCode.INVALID_LLM_CREDENTIALS_ERROR,
            ErrorCode.MISSING_LLM_STRATEGY_ERROR,
            ErrorCode.AI_TERMS_NOT_ACCEPTED_ERROR,
          ].includes(msg)
        ) {
          navigation.goBack();
        }
      });
  };

  const footerMarkup = (
    <View row>
      <View marginR-10 flex>
        <Button
          disabled={disabled}
          label={t("button.generate_ai_description")}
          isAction
          onPress={handleSubmit(handleOnSubmit)}
          icon={<Ionicons name="sparkles" size={17} color={Colors.action} />}
        ></Button>
      </View>
      <View flex>
        <Button
          disabled={
            generatedDescription === undefined ||
            generatedDescription.length === 0
          }
          label={t("button.submit_generated_ai_description")}
          backgroundColor={Colors.action}
          color="white"
          onPress={() => setProductDescription(generatedDescription || "")}
        ></Button>
      </View>
    </View>
  );

  return (
    <Page title="Generate Description (AI)" footer={footerMarkup}>
      <Stack vertical>
        <Card title="Details">
          <Stack vertical>
            <View>
              <Label
                label={t("form.ai_product_features_and_tags.label")}
                description={t("form.ai_product_features_and_tags.description")}
              ></Label>
              <Controller
                name="productFeaturesAndTags"
                control={control}
                render={({
                  field: { onChange, value, onBlur, disabled },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    readonly={disabled}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t(
                      "form.ai_product_features_and_tags.placeholder"
                    )}
                    multiline
                  />
                )}
              />
            </View>
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "tone_of_voice",
                  title: t("form.tone_of_voice.label"),
                  subTitle: t("form.tone_of_voice.description"),
                  onPress: () =>
                    navigation.navigate("SelectProductDescriptionToneScreen"),
                  componentRight: () =>
                    watchedToneOfVoice ? (
                      <Text {...textButtonProps}>{watchedToneOfVoice}</Text>
                    ) : (
                      <Text {...textButtonProps}>
                        {t("form.tone_of_voice.placeholder")}
                      </Text>
                    ),
                },
              ]}
            />
            <Divider marginVertical={0} />
            <View>
              <Label
                label={t("form.number_of_sentences.label")}
                description={t("form.number_of_sentences.description")}
              ></Label>

              <Controller
                name="numberOfSentences"
                control={control}
                render={({
                  field: { onChange, value, onBlur, disabled },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ? String(value) : ""}
                    readonly={disabled}
                    onChangeText={(value) => onChange(Number(value))}
                    keyboardType="number-pad"
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.number_of_sentences.placeholder")}
                  />
                )}
              />
            </View>
          </Stack>
        </Card>
        <Card title="Generating based on">
          <Controller
            name="generateOn"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Stack vertical>
                {GenerateFromOptions.map((option) => {
                  const isChecked = value.includes(option.value);
                  return (
                    <Checkbox
                      key={option.value}
                      value={isChecked}
                      label={option.label}
                      disabled={option.disabled}
                      onValueChange={(checked) => {
                        let newValue;
                        if (checked) {
                          // add to array
                          newValue = [...value, option.value];
                        } else {
                          // remove from array
                          newValue = value.filter((v) => v !== option.value);
                        }
                        onChange(newValue);
                      }}
                    />
                  );
                })}
                {error && <Text style={{ color: "red" }}>{error.message}</Text>}
              </Stack>
            )}
          />
        </Card>
        <View>
          <View style={{ marginBottom: Spacings.s5 }}>
            <Text variant="heading4" fontWeight="bold">
              AI GENERATED DESCRIPTION
            </Text>
          </View>
          <RichTextEditor
            value={generatedDescription || ""}
            onContentChange={(t) => setGeneratedDescription(t)}
            disabled={generatedDescription === undefined}
          />
        </View>
      </Stack>
    </Page>
  );
};

export const RichTextEditor = ({
  disabled,
  onContentChange,
  value = "",
}: {
  disabled?: boolean;
  onContentChange: (t: any) => void;
  value: string;
}) => {
  return (
    <View>
      <View
        style={{
          borderWidth: 1,
          borderColor: Colors.inputBorderColor,
          borderBottomEndRadius: 4,
          borderBottomStartRadius: 4,
          backgroundColor: disabled ? "#e4e4e4" : Colors.input,
        }}
      >
        <Editor
          style={{
            minHeight: 200,
          }}
          disabled={disabled}
          value={value}
          onChange={(t) => {
            console.log(t.target.value);
            onContentChange(t.target.value);
          }}
        ></Editor>
      </View>
    </View>
  );
};

const textButtonProps = {
  fontWeight: "bold",
  color: "action",
  variant: "body",
} as any;
